import type { ButtonHTMLAttributes } from 'react';

// Shared "Continue with Google" button. Kept presentation-only so it can
// be reused by the central auth site (hardcoded English copy -- the auth
// app is outside the i18n scope) and the shop SPA (which passes a
// translated `label`). The caller owns the click handler: the auth site
// starts the central OAuth redirect, the shop SPA starts its own
// on-origin OAuth. Both ultimately call `@pc/db` `signInWithGoogle`.
//
// The mark is the official Google "G" rendered inline as SVG so it needs
// no network asset and renders crisply at any DPR. Per Google's branding
// guidance the button is a neutral white surface with the multicolor G.

export interface GoogleButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Visible button label, e.g. "Continue with Google". */
  label: string;
}

export function GoogleButton({ label, className, disabled, ...rest }: GoogleButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={[
        'inline-flex w-full items-center justify-center gap-3 border border-rule-strong',
        'bg-paper px-4 py-2.5 text-sm font-medium text-ink transition-colors',
        'hover:bg-linen disabled:opacity-50 disabled:cursor-not-allowed',
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      <GoogleGlyph />
      <span>{label}</span>
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      focusable="false"
      className="block shrink-0"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
